import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

// Singleton socket instance shared across all components
let sharedSocket = null;
let listenerCount = 0;

const getSocket = () => {
  if (sharedSocket) return sharedSocket;
  const token = localStorage.getItem('token');
  if (!token) return null;
  sharedSocket = io(SOCKET_URL, { auth: { token } });
  return sharedSocket;
};

const destroySocket = () => {
  if (sharedSocket) {
    sharedSocket.disconnect();
    sharedSocket = null;
  }
};

export const useSocket = () => {
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    listenerCount++;

    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    if (socket.connected) setIsConnected(true);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      listenerCount--;
      if (listenerCount <= 0) {
        destroySocket();
        listenerCount = 0;
      }
    };
  }, []);

  const sendMessage = useCallback((receiver_id, message) => {
    const socket = getSocket();
    if (socket) socket.emit('send_message', { receiver_id, message });
  }, []);

  const onNewMessage = useCallback((callback) => {
    const socket = getSocket();
    if (socket) socket.on('new_message', callback);
    return () => { const s = sharedSocket; if (s) s.off('new_message', callback); };
  }, []);

  const onMessageSent = useCallback((callback) => {
    const socket = getSocket();
    if (socket) socket.on('message_sent', callback);
    return () => { const s = sharedSocket; if (s) s.off('message_sent', callback); };
  }, []);

  const markRead = useCallback((conversation_id) => {
    const socket = getSocket();
    if (socket) socket.emit('mark_read', { conversation_id });
  }, []);

  const emitTyping = useCallback((receiver_id, conversation_id) => {
    const socket = getSocket();
    if (socket) socket.emit('typing', { receiver_id, conversation_id });
  }, []);

  const emitStopTyping = useCallback((receiver_id, conversation_id) => {
    const socket = getSocket();
    if (socket) socket.emit('stop_typing', { receiver_id, conversation_id });
  }, []);

  const onTyping = useCallback((callback) => {
    const socket = getSocket();
    if (socket) socket.on('user_typing', callback);
    return () => { const s = sharedSocket; if (s) s.off('user_typing', callback); };
  }, []);

  const onUserOnline = useCallback((callback) => {
    const socket = getSocket();
    if (socket) socket.on('user_online', callback);
    return () => { const s = sharedSocket; if (s) s.off('user_online', callback); };
  }, []);

  const onUserOffline = useCallback((callback) => {
    const socket = getSocket();
    if (socket) socket.on('user_offline', callback);
    return () => { const s = sharedSocket; if (s) s.off('user_offline', callback); };
  }, []);

  return {
    isConnected,
    sendMessage,
    onNewMessage,
    onMessageSent,
    markRead,
    emitTyping,
    emitStopTyping,
    onTyping,
    onUserOnline,
    onUserOffline,
  };
};
